import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Paperclip, Sparkles, StopCircle, Settings2, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/utils/cn';
import { useChatSignalR, type AiProvider } from '@/hooks/useChatSignalR';
import { useChatStore } from '@/stores/chatStore';
import { ChatMessage } from './ChatMessage';
import { WorkPlanView } from './WorkPlanView';
import { InteractiveQuestionForm } from './InteractiveQuestionForm';
import { FileOperationCard } from './CodeBlockWithHeader';
import { ModelSelector } from './ModelSelector';
import { conversationsApi } from '@/api/conversations';
import { useToast } from '@/contexts/ToastContext';

interface ChatPanelProps {
  projectId: string;
  /** Initial conversation ID to continue (from project creation with prompt) */
  initialConversationId?: string;
  /** Initial prompt text to display */
  initialPrompt?: string;
  className?: string;
}

export function ChatPanel({ projectId, initialConversationId, initialPrompt, className }: ChatPanelProps) {
  const {
    messages,
    currentStreamingMessage,
    workPlan,
    isStreaming,
    conversationId,
    thinkingStatus,
    pendingQuestion,
    toolOperations,
    userPreferences,
    startChat,
    sendMessage,
    loadConversation,
    continueConversation,
    submitQuestionResponse,
    cancel,
    reset,
  } = useChatSignalR(projectId);

  const { setUserPreferences } = useChatStore();
  const toast = useToast();

  // Work Plan approval/rejection handlers
  const handleApproveWorkPlan = useCallback(async () => {
    if (!workPlan?.id) return;
    try {
      await conversationsApi.approveWorkPlan(workPlan.id);
      toast.success('Work Plan Approved', 'The work plan has been approved and execution will begin.');
    } catch (error) {
      console.error('Failed to approve work plan:', error);
      toast.error('Error', 'Failed to approve work plan');
    }
  }, [workPlan?.id, toast]);

  const handleRejectWorkPlan = useCallback(async (reason: string) => {
    if (!workPlan?.id) return;
    try {
      await conversationsApi.rejectWorkPlan(workPlan.id, reason);
      toast.info('Work Plan Rejected', 'The work plan has been rejected.');
    } catch (error) {
      console.error('Failed to reject work plan:', error);
      toast.error('Error', 'Failed to reject work plan');
    }
  }, [workPlan?.id, toast]);

  const [input, setInput] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState('deepseek-chat');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // Extract provider from model ID for API calls
  const getProviderFromModelId = (modelId: string): AiProvider => {
    if (modelId.startsWith('claude')) return 'Claude';
    return 'Deepseek';
  };
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Use refs to track trigger state - more reliable than useState for cross-effect coordination
  const triggeredForProjectRef = useRef<string | null>(null);
  const triggeredConversationRef = useRef<string | null>(null);

  // Load and process existing conversation
  const handleLoadConversation = useCallback(async (convId: string, prompt?: string) => {
    console.log('[ChatPanel] handleLoadConversation called', { convId, prompt });
    setIsLoadingConversation(true);
    setIsInitializing(true);

    try {
      // First, try to load existing messages
      const result = await loadConversation(convId);
      console.log('[ChatPanel] loadConversation result', {
        hasPendingResponse: result.hasPendingResponse,
        messagesCount: result.messages.length
      });

      // If there's a pending response (no AI reply yet), continue the conversation
      if (result.hasPendingResponse) {
        console.log('[ChatPanel] Continuing pending conversation');
        await continueConversation(convId, undefined, { autoApprove: true });
      } else {
        console.log('[ChatPanel] Conversation already has AI response, just showing messages');
      }
    } catch (err) {
      console.error('[ChatPanel] Error loading conversation:', err);
    } finally {
      setIsLoadingConversation(false);
      setIsInitializing(false);
    }
  }, [loadConversation, continueConversation]);

  // Combined effect for reset and auto-trigger
  // This ensures proper sequencing: reset first, then load/trigger
  useEffect(() => {
    console.log('[ChatPanel] Main effect running', {
      projectId,
      initialConversationId,
      triggeredForProject: triggeredForProjectRef.current,
      triggeredConversation: triggeredConversationRef.current,
      isStreaming,
      isInitializing,
      isLoadingConversation,
      messagesLength: messages.length,
    });

    // If projectId changed, reset everything
    if (triggeredForProjectRef.current !== projectId) {
      console.log('[ChatPanel] Project changed, resetting', {
        from: triggeredForProjectRef.current,
        to: projectId
      });
      reset();
      triggeredForProjectRef.current = projectId;
      triggeredConversationRef.current = null;
    }

    // Check if we should load/trigger the conversation
    const shouldTrigger =
      initialConversationId &&
      triggeredConversationRef.current !== initialConversationId &&
      !isStreaming &&
      !isInitializing &&
      !isLoadingConversation &&
      messages.length === 0;

    console.log('[ChatPanel] Should trigger?', {
      shouldTrigger,
      initialConversationId,
      alreadyTriggered: triggeredConversationRef.current,
      isStreaming,
      isInitializing,
      isLoadingConversation,
      messagesLength: messages.length
    });

    if (shouldTrigger) {
      console.log('[ChatPanel] Triggering handleLoadConversation', {
        initialConversationId,
        initialPrompt
      });
      triggeredConversationRef.current = initialConversationId;

      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        handleLoadConversation(initialConversationId, initialPrompt);
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [projectId, initialConversationId, initialPrompt, isStreaming, isInitializing, isLoadingConversation, messages.length, reset, handleLoadConversation]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentStreamingMessage]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    const message = input.trim();
    setInput('');
    const provider = getProviderFromModelId(selectedModelId);

    if (conversationId) {
      await sendMessage(message, conversationId, { autoApprove, provider });
    } else {
      await startChat(message, { autoApprove, provider });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Show initializing state ONLY when waiting for first content - not when streaming has started
  const hasContent = messages.length > 0 || currentStreamingMessage;
  const showInitializing = !hasContent && (isInitializing || isLoadingConversation || isStreaming);

  return (
    <div className={cn('flex flex-col h-full bg-white dark:bg-dark-800', className)}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        {showInitializing ? (
          <InitializingScreen prompt={initialPrompt} thinkingStatus={thinkingStatus} />
        ) : messages.length === 0 && !currentStreamingMessage ? (
          <WelcomeScreen onPromptSelect={(prompt) => setInput(prompt)} />
        ) : (
          <div className="p-4 space-y-4">
            {/* Show thinking status inline when we have content */}
            {thinkingStatus && (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 p-3 bg-gray-50 dark:bg-dark-900 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin" />
                {thinkingStatus}
              </div>
            )}
            {messages.map((message, index) => (
              <ChatMessage
                key={index}
                message={message}
                showThinking={userPreferences.showThinking}
                thinkingDefaultExpanded={userPreferences.thinkingDefaultExpanded}
              />
            ))}
            {currentStreamingMessage && (
              <ChatMessage
                message={{
                  role: 'Assistant',
                  content: currentStreamingMessage,
                  createdAt: new Date(),
                }}
                isStreaming
                showThinking={userPreferences.showThinking}
                thinkingDefaultExpanded={userPreferences.thinkingDefaultExpanded}
              />
            )}

            {/* Active tool operations */}
            {toolOperations.filter(op => op.status === 'running').map((op) => (
              <FileOperationCard
                key={op.id}
                action="reading"
                filePath={op.description}
                isLoading
                className="my-2"
              />
            ))}

            {/* Pending question from AI */}
            {pendingQuestion && conversationId && (
              <InteractiveQuestionForm
                questionId={pendingQuestion.questionId}
                question={pendingQuestion.question}
                options={pendingQuestion.options?.map(o => ({ id: o.id, label: o.label, description: o.description }))}
                allowMultiple={pendingQuestion.allowMultiple}
                allowCustom={pendingQuestion.allowCustom}
                context={pendingQuestion.context}
                onSubmit={async (_questionId, response) => {
                  // Combine selected options and custom text into a single response
                  const allResponses = [
                    ...response.selectedOptions,
                    ...(response.customText ? [response.customText] : [])
                  ];
                  await submitQuestionResponse(pendingQuestion.questionId, allResponses, conversationId);
                }}
                className="my-4"
              />
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Work Plan Panel */}
      {workPlan && (
        <div className="border-t border-gray-200 dark:border-dark-700">
          <WorkPlanView
            workPlan={workPlan}
            onApprove={handleApproveWorkPlan}
            onReject={handleRejectWorkPlan}
          />
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-gray-200 dark:border-dark-700 p-4">
        {/* Settings Toggle */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <Settings2 className="w-4 h-4" />
              <span>Options</span>
            </button>
            <ModelSelector
              value={selectedModelId}
              onChange={setSelectedModelId}
            />
          </div>
          {isStreaming && (
            <Button variant="ghost" size="sm" onClick={cancel}>
              <StopCircle className="w-4 h-4 mr-2" />
              Stop
            </Button>
          )}
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="mb-3 p-3 bg-gray-50 dark:bg-dark-900 rounded-lg space-y-4">
            {/* Thinking Display Settings */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {userPreferences.showThinking ? (
                    <Eye className="w-4 h-4 text-purple-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Show AI thinking
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Display the AI's reasoning process
                    </div>
                  </div>
                </div>
                <Switch
                  checked={userPreferences.showThinking}
                  onChange={(checked) => setUserPreferences({ showThinking: checked })}
                  size="sm"
                />
              </div>

              {userPreferences.showThinking && (
                <div className="flex items-center justify-between pl-6">
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Expand by default
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Show thinking content expanded
                    </div>
                  </div>
                  <Switch
                    checked={userPreferences.thinkingDefaultExpanded}
                    onChange={(checked) => setUserPreferences({ thinkingDefaultExpanded: checked })}
                    size="sm"
                  />
                </div>
              )}
            </div>

            {/* Auto-approve switch */}
            <Switch
              checked={autoApprove}
              onChange={setAutoApprove}
              label="Auto-approve work plans"
              description="Automatically approve AI-generated work plans without manual review"
              size="sm"
            />
          </div>
        )}

        {/* Input Form */}
        <form onSubmit={handleSubmit} className="relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want to build..."
            rows={1}
            disabled={isStreaming}
            className={cn(
              'w-full pr-24 pl-4 py-3 resize-none rounded-xl border border-gray-200 dark:border-dark-700',
              'bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-white',
              'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-2">
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Attach files"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || isStreaming}
              className="rounded-lg"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>

        <p className="mt-2 text-xs text-center text-gray-400 dark:text-gray-500">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}

function InitializingScreen({ prompt, thinkingStatus }: { prompt?: string; thinkingStatus?: string | null }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spark-400 to-spark-600 flex items-center justify-center mb-6">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {thinkingStatus || 'AI is getting ready...'}
      </h2>
      {prompt && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-dark-900 rounded-xl max-w-md">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Your request:</p>
          <p className="text-gray-700 dark:text-gray-300">{prompt}</p>
        </div>
      )}
      <p className="mt-6 text-gray-500 dark:text-gray-400 max-w-md">
        The AI is analyzing your request and preparing to build your application.
        You'll see real-time progress as files are created.
      </p>
    </div>
  );
}

function WelcomeScreen({ onPromptSelect }: { onPromptSelect: (prompt: string) => void }) {
  const prompts = [
    {
      title: 'Create a landing page',
      description: 'Build a modern landing page with hero section, features, and CTA',
      prompt: 'Create a modern landing page with a hero section, features grid, testimonials, and a call-to-action section. Use a clean, professional design.',
    },
    {
      title: 'Build authentication',
      description: 'Add user login, registration, and password reset',
      prompt: 'Implement a complete authentication system with login, registration, password reset, and email verification. Include protected routes and session management.',
    },
    {
      title: 'Create a REST API',
      description: 'Build CRUD endpoints with validation',
      prompt: 'Create a RESTful API with CRUD operations for managing resources. Include input validation, error handling, and proper HTTP status codes.',
    },
    {
      title: 'Add a dashboard',
      description: 'Build an admin dashboard with charts and stats',
      prompt: 'Create an admin dashboard with statistics cards, charts showing data over time, a data table with filtering and pagination, and a sidebar navigation.',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spark-400 to-spark-600 flex items-center justify-center mb-6">
        <Sparkles className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        What would you like to build?
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">
        Describe your idea and I'll help you build it. I can generate code, create databases,
        and deploy your application.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
        {prompts.map((item, index) => (
          <button
            key={index}
            onClick={() => onPromptSelect(item.prompt)}
            className="p-4 text-left rounded-xl border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors"
          >
            <h3 className="font-medium text-gray-900 dark:text-white mb-1">
              {item.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {item.description}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
